import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button, Text, Title, CardListSkeleton } from '@/shared/ui';
import { DataState } from '@/shared/ui/components/basic/data-state/DataState';
import StatusBadge from '@/shared/ui/components/basic/StatusBadge';
import { OpenAPIHousien } from '@services/requests';
import { request as __request } from '@services/requests/core/request';
import { searchStatusToTone } from '@/shared/utils/partnerships-status';
import { buildSearchTitle } from '@/shared/utils/partnerships-search-record';
import { isTerminalStatus, MAX_POLL_ATTEMPTS, normalizeSearchStatus } from '@/shared/utils/partnerships-poll';
import { ResultsList } from '../components/ResultsList';
import { SearchSummaryStats } from '../components/SearchSummaryStats';
import type { PartnershipResult, SearchParams, SearchStatus } from '../context/partnerships-wizard-types';

interface SearchDetail {
  search_id: string;
  status: SearchStatus;
  created_at: string;
  city: string;
  neighbourhood?: string;
  query?: SearchParams;
  results?: PartnershipResult[];
  properties_retrieved?: number;
  agencies_found?: number;
}

/**
 * Formats a date string into a human-readable relative or absolute form.
 *
 * @param iso - The ISO date string.
 * @returns A readable date string.
 */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const POLL_INTERVAL_MS = 3000;

/**
 * Derives the localized labels needed for the auto-title from a search query.
 *
 * @param query - The stored search parameters, if any.
 * @param t - The translation function.
 * @returns The property-type and transaction labels for the title.
 */
function titleLabels(
  query: SearchParams | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { propertyType?: string; transaction?: string } {
  const propertyType = query?.property_type
    ? t(`property_types.${query.property_type}`, { defaultValue: query.property_type })
    : undefined;
  const transaction = query?.transaction
    ? t(`transactions.${query.transaction}`, { defaultValue: query.transaction })
    : undefined;
  return { propertyType, transaction };
}

/**
 * Page showing a specific search result by id, with status badge, params
 * summary, result list, and a "Duplicate as new search" button.
 *
 * @returns The search detail page.
 */
export default function PartnershipsSearchDetailPage() {
  const { t } = useTranslation('features/partnerships');
  const navigate = useNavigate();
  const { agency_id, search_id } = useParams<{ agency_id: string; search_id: string }>();

  const [detail, setDetail] = useState<SearchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!agency_id || !search_id) return;
    try {
      const resp = await __request(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies/{agencyId}/partnerships/searches/{searchId}',
        path: { agencyId: agency_id, searchId: search_id },
      });
      const data = resp as SearchDetail;
      data.status = normalizeSearchStatus(data.status) as SearchStatus;
      if (!data.city && data.query?.city) data.city = data.query.city;
      if (!data.neighbourhood && data.query?.neighbourhood) data.neighbourhood = data.query.neighbourhood;
      setDetail(data);
      setLoading(false);
      if (isTerminalStatus(data.status)) stopPolling();
    } catch {
      setError(t('detail.load_error'));
      setLoading(false);
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency_id, search_id, stopPolling]);

  useEffect(() => {
    attemptsRef.current = 0;
    fetchDetail();

    pollRef.current = setInterval(() => {
      attemptsRef.current += 1;
      if (attemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling();
        setError(t('results.search_timeout'));
        setLoading(false);
        return;
      }
      fetchDetail();
    }, POLL_INTERVAL_MS);

    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency_id, search_id]);

  const handleDuplicate = () => {
    if (!detail?.query) return;
    const params = new URLSearchParams();
    params.set('seed', JSON.stringify(detail.query));
    navigate(`../partnerships/new?${params.toString()}`);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-[var(--space-3)] px-(--space-6) pt-(--space-6) pb-(--space-3)">
        <Button variant="ghost" onClick={() => navigate('../partnerships')}>
          ← {t('detail.back_to_history')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-(--space-6) pb-(--space-6)">
        <DataState
          isLoading={loading}
          isError={!!error}
          isEmpty={!detail}
          loadingFallback={<CardListSkeleton count={3} />}
          errorState={
            <Text variant="body" c="dimmed">{error}</Text>
          }
          emptyState={
            <Text variant="body" c="dimmed">{t('detail.not_found')}</Text>
          }
        >
          {detail && (
            <div className="flex flex-col gap-[var(--space-5)]">
              <div className="flex flex-col gap-[var(--space-2)]">
                <div className="flex flex-wrap items-center gap-[var(--space-3)]">
                  <Title order={1} size="lg" data-testid="search-title">
                    {buildSearchTitle(
                      { city: detail.city, neighbourhood: detail.neighbourhood, query: detail.query },
                      titleLabels(detail.query, t),
                      t('page_title'),
                    )}
                  </Title>
                  <StatusBadge tone={searchStatusToTone(detail.status)}>
                    {t(`history.status_${detail.status}`)}
                  </StatusBadge>
                  <Text variant="body" size="sm" c="dimmed">
                    {formatDate(detail.created_at)}
                  </Text>
                </div>
                <SearchSummaryStats
                  propertiesRetrieved={detail.properties_retrieved}
                  results={detail.results ?? []}
                />
              </div>

              {detail.query && (
                <div className="flex flex-wrap gap-[var(--space-2)]">
                  {detail.query.property_type && (
                    <StatusBadge tone="neutral">{detail.query.property_type}</StatusBadge>
                  )}
                  {detail.query.transaction && (
                    <StatusBadge tone="neutral">{detail.query.transaction}</StatusBadge>
                  )}
                  {detail.query.bedrooms && (
                    <StatusBadge tone="neutral">
                      {detail.query.bedrooms} {t('detail.bedrooms')}
                    </StatusBadge>
                  )}
                </div>
              )}

              <div className="flex items-center gap-[var(--space-3)]">
                <Button variant="outlined" onClick={handleDuplicate}>
                  {t('detail.duplicate')}
                </Button>
              </div>

              <Title order={2} size="sm" data-testid="search-results-title">
                {t('detail.results_title')}
              </Title>
              <ResultsList
                results={detail.results ?? []}
                searched={detail.status === 'completed' || detail.status === 'failed'}
                loading={detail.status === 'pending' || detail.status === 'running'}
                error={detail.status === 'failed' ? t('results.search_failed') : null}
              />
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}
